import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Search,
  Building2,
  MapPin,
  PlugZap,
  Loader2,
  Check,
  X,
  ChevronLeft,
  ArrowLeft,
  AlertCircle,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import Api from "../../services/Api";
import { fetchHierarchyCached } from "../../lib/hooks/useHierarchy";
import { fetchFleetDataCached } from "../../lib/context/FleetDataProvider";
import "./CreateHub.scss";
const STATION_DETAIL_URL = "/service/iqevse-csms/stations/v1";
const CREATE_STATION_URL = "/service/iqevse-csms/stations/v1/create";

const STEPS = [
  { label: "Station Info & Sites", icon: Building2 },
  { label: "Location & Address", icon: MapPin },
];

export default function CreateStationForm({ onBack, onCreated, initialData }) {
  const queryClient = useQueryClient();
  const isEditMode = !!initialData;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: initialData?.stationName || initialData?.name || "",
    maxBreakerCurrent: initialData?.maxBreakerCurrent || "",
    selectedSites: initialData?.siteIds || (initialData?.sites || []).map(s => s.siteId || s.id) || [],
    lat: initialData?.latitude || initialData?.lat || 37.7749,
    lng: initialData?.longitude || initialData?.lng || -122.4194,
    address: "",
    city: "",
    state: "",
    zip: "",
  });
  const [siteSearch, setSiteSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (initialData?.address) {
      const parts = initialData.address.split(",").map(s => s.trim());
      setForm(f => ({
        ...f,
        address: parts[0] || "",
        city: parts[1] || "",
        state: parts[2] || "",
        zip: parts[3] || "",
      }));
    }
  }, [initialData]);

  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchSites() {
      setSitesLoading(true);
      try {
        const stationsArr = await fetchHierarchyCached(queryClient);
        if (cancelled) return;

        const unassignedStation = stationsArr.find(
          station => (station.stationName || station.name || "").toLowerCase() === "unassigned"
        );

        if (unassignedStation && unassignedStation.sites && unassignedStation.sites.length > 0) {
          const siteMap = new Map();
          unassignedStation.sites.forEach(site => {
            const sid = site.siteId || site.id;
            if (!sid || siteMap.has(sid)) return;
            const chargers = site.chargers || [];
            const connectedCount = chargers.filter(c => c.isConnected).length;
            siteMap.set(sid, {
              siteId: sid,
              displayName: site.siteName || site.displayName || site.name || `Site ${sid}`,
              country: site.country || "",
              chargerCount: chargers.length,
              connectedCount,
              assignedTo: null,
            });
          });
          const sitesArray = Array.from(siteMap.values());
          setSites(sitesArray);
        } else {
          const chargers = await fetchFleetDataCached(queryClient).catch(() => []);

          if (cancelled) return;

          const assignedSites = new Set();
          stationsArr.forEach(station => {
            const stationName = (station.stationName || station.name || "").toLowerCase();
            if (stationName !== "unassigned") {
              const stationSites = station.sites || [];
              stationSites.forEach(site => {
                const sid = site.siteId || site.id;
                if (sid) {
                  assignedSites.add(sid);
                }
              });
            }
          });

          const siteMap = new Map();
          chargers.forEach(c => {
            const sid = c.siteId;
            if (!sid) return;
            if (!siteMap.has(sid)) {
              const isAssigned = assignedSites.has(sid);
              siteMap.set(sid, {
                siteId: sid,
                displayName: c.siteName || `Site ${sid}`,
                country: c.country || "",
                chargerCount: 0,
                connectedCount: 0,
                assignedTo: isAssigned ? "Assigned Station" : null,
              });
            }
            const entry = siteMap.get(sid);
            entry.chargerCount += 1;
            if (c.isConnected) entry.connectedCount += 1;
          });
          const sitesArray = Array.from(siteMap.values());
          setSites(sitesArray);
        }
      } catch (hierarchyErr) {
        console.warn("Hierarchy API failed, falling back to ev_charger API:", hierarchyErr);
        try {
          const chargers = await fetchFleetDataCached(queryClient);
          if (cancelled) return;

          const siteMap = new Map();
          chargers.forEach(c => {
            const sid = c.siteId;
            if (!sid) return;
            if (!siteMap.has(sid)) {
              siteMap.set(sid, {
                siteId: sid,
                displayName: c.siteName || `Site ${sid}`,
                country: c.country || "",
                chargerCount: 0,
                connectedCount: 0,
                assignedTo: null,
              });
            }
            const entry = siteMap.get(sid);
            entry.chargerCount += 1;
            if (c.isConnected) entry.connectedCount += 1;
          });
          const sitesArray = Array.from(siteMap.values());
          setSites(sitesArray);
        } catch (fleetErr) {
          console.error("Both hierarchy and ev_charger APIs failed:", fleetErr);
        }
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    }
    fetchSites();
    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  const update = useCallback((key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  const toggleSite = useCallback(
    siteId => {
      const site = sites.find(s => s.siteId === siteId);
      if (site?.assignedTo) return;
      setForm(prev => {
        const selected = prev.selectedSites.includes(siteId)
          ? prev.selectedSites.filter(s => s !== siteId)
          : [...prev.selectedSites, siteId];
        return { ...prev, selectedSites: selected };
      });
    },
    [sites]
  );

  const removeSite = useCallback(siteId => {
    setForm(prev => ({
      ...prev,
      selectedSites: prev.selectedSites.filter(s => s !== siteId),
    }));
  }, []);

  const filteredSites = useMemo(() => {
    if (!siteSearch.trim()) return sites;
    const q = siteSearch.toLowerCase();
    return sites.filter(
      s =>
        String(s.siteId).toLowerCase().includes(q) ||
        s.displayName.toLowerCase().includes(q) ||
        s.country.toLowerCase().includes(q)
    );
  }, [sites, siteSearch]);

  const canCreate = form.name.trim().length > 0 && form.selectedSites.length > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    const fullAddress = [form.address, form.city, form.state, form.zip].filter(Boolean).join(", ");
    const payload = {
      stationName: form.name.trim(),
      siteIds: form.selectedSites.map(id => (typeof id === "string" ? parseInt(id, 10) : id)),
    };
    const parsedBreaker = Number(form.maxBreakerCurrent);
    if (
      form.maxBreakerCurrent !== "" &&
      form.maxBreakerCurrent != null &&
      !isNaN(parsedBreaker) &&
      parsedBreaker >= 0
    ) {
      payload.maxBreakerCurrent = parsedBreaker;
    }
    if (fullAddress) payload.address = fullAddress;
    if (form.address.trim()) {
      payload.latitude = form.lat;
      payload.longitude = form.lng;
    }

    try {
      if (isEditMode) {
        const editId = initialData.stationId || initialData.id;
        const fullPayload = { ...initialData, ...payload };
        await Api.put(`${STATION_DETAIL_URL}/${editId}`, fullPayload);
        onCreated(editId);
      } else {
        const result = await Api.post(CREATE_STATION_URL, payload);
        const newId = result?.stationId || result?.id || result?.data?.stationId || result?.data?.id;
        onCreated(newId || "");
      }
    } catch (err) {
      console.error("Station save failed:", err);
      setSubmitError(err?.response?.data?.message || err.message || "Failed to save station");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="create-hub">
      <button className="manage-station__back-btn" onClick={onBack}>
        <ArrowLeft size={16} /> {isEditMode ? "Back to Station" : "Back to Stations"}
      </button>

      <div className="create-hub__stepper">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = i === step;
          const isDone = i < step;
          return (
            <div
              key={s.label}
              className={`create-hub__step ${isActive ? "create-hub__step--active" : ""} ${isDone ? "create-hub__step--done" : ""}`}
            >
              <div className="create-hub__step-circle">{isDone ? <Check size={16} /> : <Icon size={16} />}</div>
              <span className="create-hub__step-label">{s.label}</span>
              {i < STEPS.length - 1 && <div className="create-hub__step-line" />}
            </div>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          className="create-hub__content"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
        >
          {step === 0 && (
            <StepInfoAndSites
              form={form}
              update={update}
              siteSearch={siteSearch}
              setSiteSearch={setSiteSearch}
              filteredSites={filteredSites}
              sitesLoading={sitesLoading}
              toggleSite={toggleSite}
              removeSite={removeSite}
              sites={sites}
            />
          )}
          {step === 1 && <StepLocation form={form} update={update} />}
        </motion.div>
      </AnimatePresence>

      {submitError && (
        <motion.div className="create-hub__error" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
          <AlertCircle size={16} />
          <span>{submitError}</span>
        </motion.div>
      )}

      <div className="create-hub__nav">
        {step === 0 ? (
          <>
            <button
              className="create-hub__btn create-hub__btn--secondary"
              onClick={() => setStep(1)}
              disabled={!canCreate}
            >
              <MapPin size={16} /> Add Location (Optional)
            </button>
            <button
              className="create-hub__btn create-hub__btn--primary"
              onClick={handleSubmit}
              disabled={!canCreate || submitting}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="create-hub__spinner" /> {isEditMode ? "Saving..." : "Creating..."}
                </>
              ) : (
                <>
                  <Check size={16} /> {isEditMode ? "Save Station" : "Create Station"}
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <button className="create-hub__btn create-hub__btn--secondary" onClick={() => setStep(0)}>
              <ChevronLeft size={16} /> Back
            </button>
            <button
              className="create-hub__btn create-hub__btn--primary"
              onClick={handleSubmit}
              disabled={!canCreate || submitting}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="create-hub__spinner" /> {isEditMode ? "Saving..." : "Creating..."}
                </>
              ) : (
                <>
                  <Check size={16} /> {isEditMode ? "Save Station" : "Create Station"}
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function StepInfoAndSites({
  form,
  update,
  siteSearch,
  setSiteSearch,
  filteredSites,
  sitesLoading,
  toggleSite,
  removeSite,
  sites,
}) {
  return (
    <div className="create-hub__form-card">
      <h3 className="create-hub__card-title">
        <Building2 size={20} /> Station Information
      </h3>
      <p className="create-hub__card-desc">Enter the station name and select sites to include.</p>

      <div className="create-hub__field">
        <label className="create-hub__label" htmlFor="hub-name">
          Station Name *
        </label>
        <input
          id="hub-name"
          className="create-hub__input"
          type="text"
          placeholder="e.g. Bay Area Station"
          value={form.name}
          onChange={e => update("name", e.target.value)}
        />
      </div>

      <div className="create-hub__field">
        <label className="create-hub__label" htmlFor="hub-breaker">
          Max Breaker Current (A)
        </label>
        <input
          id="hub-breaker"
          className="create-hub__input"
          type="number"
          min="0"
          step="1"
          placeholder="e.g. 120"
          value={form.maxBreakerCurrent}
          onChange={e => update("maxBreakerCurrent", e.target.value)}
        />
      </div>

      <div className="create-hub__divider" />
      <h3 className="create-hub__card-title">
        <PlugZap size={20} /> Select Sites
      </h3>
      <p className="create-hub__card-desc">Choose one or more sites to assign to this station.</p>

      {form.selectedSites.length > 0 && (
        <div className="create-hub__chips">
          {form.selectedSites.map(id => {
            const site = sites.find(s => s.siteId === id);
            return (
              <motion.span
                key={id}
                className="create-hub__chip"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
              >
                <PlugZap size={12} />
                {site?.displayName || `Site ${id}`}
                <button className="create-hub__chip-remove" onClick={() => removeSite(id)}>
                  <X size={12} />
                </button>
              </motion.span>
            );
          })}
        </div>
      )}

      <div className="create-hub__search-box">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search sites by ID, name, or country..."
          value={siteSearch}
          onChange={e => setSiteSearch(e.target.value)}
        />
      </div>

      <div className="create-hub__station-list">
        {sitesLoading ? (
          <div className="create-hub__loading">
            <Loader2 size={20} className="create-hub__spinner" />
            <span>Loading sites...</span>
          </div>
        ) : (
          <>
            {filteredSites.map(site => {
              const isSelected = form.selectedSites.includes(site.siteId);
              const isAssigned = !!site.assignedTo;
              return (
                <div
                  key={site.siteId}
                  className={`create-hub__station-item ${isSelected ? "create-hub__station-item--selected" : ""} ${isAssigned ? "create-hub__station-item--disabled" : ""}`}
                  role={isAssigned ? "presentation" : "button"}
                  tabIndex={isAssigned ? -1 : 0}
                  onClick={() => !isAssigned && toggleSite(site.siteId)}
                  onKeyDown={e => {
                    if (!isAssigned && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      toggleSite(site.siteId);
                    }
                  }}
                >
                  <div className="create-hub__station-check">{isSelected && <Check size={14} />}</div>
                  <div className="create-hub__station-info">
                    <span className="create-hub__station-name">{site.displayName}</span>
                    <span className="create-hub__station-meta">
                      Site ID: {site.siteId} &middot; {site.country || "N/A"} &middot; {site.chargerCount} chargers
                      {isAssigned && <span className="create-hub__assigned-badge">&middot; {site.assignedTo}</span>}
                    </span>
                  </div>
                  <div className="create-hub__station-badge">
                    {isAssigned ? (
                      <span className="create-hub__status create-hub__status--assigned">Assigned</span>
                    ) : (
                      <span
                        className={`create-hub__status create-hub__status--${site.chargerCount === 0 ? "grey" : site.connectedCount === site.chargerCount ? "green" : "amber"}`}
                      >
                        {site.connectedCount}/{site.chargerCount} online
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredSites.length === 0 && <div className="create-hub__empty">No sites match your search.</div>}
          </>
        )}
      </div>
    </div>
  );
}

function StepLocation({ form, update }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const geocodeTimerRef = useRef(null);

  const reverseGeocode = useCallback(
    async (lat, lng) => {
      clearTimeout(geocodeTimerRef.current);
      geocodeTimerRef.current = setTimeout(async () => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
            { headers: { "Accept-Language": "en" } }
          );
          const data = await res.json();
          if (data?.address) {
            const a = data.address;
            const road = [a.house_number, a.road].filter(Boolean).join(" ");
            update("address", road || a.neighbourhood || a.suburb || "");
            update("city", a.city || a.town || a.village || a.county || "");
            update("state", a.state || a.region || "");
            update("zip", a.postcode || "");
          }
        } catch (err) {
          console.warn("Reverse geocode failed:", err);
        }
      }, 400);
    },
    [update]
  );

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [form.lat, form.lng],
      zoom: 12,
      scrollWheelZoom: true,
      attributionControl: false,
    });

    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      subdomains: "abcd",
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker([form.lat, form.lng], {
      draggable: true,
      icon: L.divIcon({
        className: "create-hub__map-pin",
        html: `<div class="create-hub__pin-inner"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f47c20" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 24],
      }),
    }).addTo(map);

    marker.on("dragend", () => {
      const pos = marker.getLatLng();
      update("lat", pos.lat);
      update("lng", pos.lng);
      reverseGeocode(pos.lat, pos.lng);
    });

    map.on("click", e => {
      marker.setLatLng(e.latlng);
      update("lat", e.latlng.lat);
      update("lng", e.latlng.lng);
      reverseGeocode(e.latlng.lat, e.latlng.lng);
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      clearTimeout(geocodeTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="create-hub__form-card">
      <h3 className="create-hub__card-title">
        <MapPin size={20} /> Location & Address
      </h3>
      <p className="create-hub__card-desc">Click on the map or drag the pin to set the station location.</p>

      <div className="create-hub__map-wrapper">
        <div ref={mapContainerRef} className="create-hub__map" />
        <div className="create-hub__coords">
          <span>Lat: {form.lat.toFixed(4)}</span>
          <span>Lng: {form.lng.toFixed(4)}</span>
        </div>
      </div>

      <div className="create-hub__field-grid">
        <div className="create-hub__field create-hub__field--full">
          <label className="create-hub__label" htmlFor="hub-addr">
            Street Address
          </label>
          <input
            id="hub-addr"
            className="create-hub__input"
            type="text"
            placeholder="e.g. 500 Folsom St"
            value={form.address}
            onChange={e => update("address", e.target.value)}
          />
        </div>
        <div className="create-hub__field">
          <label className="create-hub__label" htmlFor="hub-city">
            City
          </label>
          <input
            id="hub-city"
            className="create-hub__input"
            type="text"
            placeholder="e.g. San Francisco"
            value={form.city}
            onChange={e => update("city", e.target.value)}
          />
        </div>
        <div className="create-hub__field">
          <label className="create-hub__label" htmlFor="hub-state">
            State
          </label>
          <input
            id="hub-state"
            className="create-hub__input"
            type="text"
            placeholder="e.g. CA"
            value={form.state}
            onChange={e => update("state", e.target.value)}
          />
        </div>
        <div className="create-hub__field">
          <label className="create-hub__label" htmlFor="hub-zip">
            ZIP Code
          </label>
          <input
            id="hub-zip"
            className="create-hub__input"
            type="text"
            placeholder="e.g. 94105"
            value={form.zip}
            onChange={e => update("zip", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

const formShape = PropTypes.shape({
  name: PropTypes.string,
  maxBreakerCurrent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedSites: PropTypes.array,
  lat: PropTypes.number,
  lng: PropTypes.number,
  address: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.string,
});

CreateStationForm.propTypes = {
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
  initialData: PropTypes.object,
};

StepInfoAndSites.propTypes = {
  form: formShape.isRequired,
  update: PropTypes.func.isRequired,
  siteSearch: PropTypes.string.isRequired,
  setSiteSearch: PropTypes.func.isRequired,
  filteredSites: PropTypes.array.isRequired,
  sitesLoading: PropTypes.bool.isRequired,
  toggleSite: PropTypes.func.isRequired,
  removeSite: PropTypes.func.isRequired,
  sites: PropTypes.array.isRequired,
};

StepLocation.propTypes = {
  form: formShape.isRequired,
  update: PropTypes.func.isRequired,
};
